import { useCallback, useContext, useMemo } from 'react';
import { createId } from '@paralleldrive/cuid2';
import type { Chat } from '@trinity/chat';
import { ChatFileUploadStatus, ChatQueryStatus } from '@trinity/chat';
import { SidePanelExpandedContext } from '@trinity/chat-components/components/BrandInsights/context';
import { transformRemirrorState } from '@trinity/chat-components/components/Remirror/utils/transform-remirror-state';
import {
  ActivePageContext,
  SubmitMessageContext,
  ChatSessionInputContext,
  ChatSessionFileUploadsContext,
  IsPreparingUploadContext,
  ChatHasAccessContext,
  ChatSessionQueriesContext,
  ChatSessionPendingHintPromptIdentifierContext,
  EditorDocContext,
} from './context';
import { useSelectedQueryParentIdentifier } from './hooks/useSelectedQueryParentIdentifier';
import { useSubmitQueryMutation } from './hooks/useSubmitQueryMutation';
import type { Context } from './interfaces.context';

type Props = {
  children: React.ReactNode;
};

/**
 * Returns the identifier of the first in-progress query, or null if none.
 */
const getInProgressQueryIdentifier = (queries: Record<string, Chat.Query>): string | null => {
  if (!queries) return null;

  const found = Object.values(queries).find(
    (query) =>
      query.status === ChatQueryStatus.Loading ||
      query.status === ChatQueryStatus.LoadingExtended ||
      query.status === ChatQueryStatus.Streaming
  );

  return found?.identifier ?? null;
};

export const SubmitMessageContainer = (props: Props) => {
  const input = useContext(ChatSessionInputContext);
  const fileUploads = useContext(ChatSessionFileUploadsContext);
  const queries = useContext(ChatSessionQueriesContext);
  const pendingHintPromptIdentifier = useContext(ChatSessionPendingHintPromptIdentifierContext);
  const editorDocRef = useContext(EditorDocContext);
  const getSelectedQueryParentIdentifier = useSelectedQueryParentIdentifier();
  const hasAccess = useContext(ChatHasAccessContext);

  const { isPending, mutateAsync } = useSubmitQueryMutation();
  const isPreparingUpload = useContext(IsPreparingUploadContext);
  const { clearPage } = useContext(ActivePageContext);
  const [isExpanded] = useContext(SidePanelExpandedContext);

  const handleSubmit = useCallback(() => {
    if (!isExpanded) {
      clearPage();
    }

    const identifier = createId();
    const parentQueryIdentifier = getSelectedQueryParentIdentifier();

    const editorDoc = editorDocRef.current;
    const { text: queryText, mentions } = editorDoc
      ? transformRemirrorState(editorDoc)
      : { text: input, mentions: [] };

    return mutateAsync({
      action: null,
      identifier,
      parentQueryIdentifier,
      query: queryText,
      promptIdentifier: pendingHintPromptIdentifier ?? undefined,
      mentions: mentions.length > 0 ? mentions : undefined,
    });
  }, [
    clearPage,
    editorDocRef,
    getSelectedQueryParentIdentifier,
    input,
    mutateAsync,
    isExpanded,
    pendingHintPromptIdentifier,
  ]);

  const submitState = useMemo((): Context.SubmitMessageMetadata => {
    const inProgressIdentifier = getInProgressQueryIdentifier(queries);

    if (isPending || inProgressIdentifier !== null) {
      return { status: 'submitting-query', queryIdentifier: inProgressIdentifier };
    }

    if (
      isPreparingUpload ||
      fileUploads.some((s) => s.items.some((i) => i.status !== ChatFileUploadStatus.Ready))
    ) {
      return { status: 'uploading-file' };
    }

    if (input.trim().length === 0 || !hasAccess) {
      return { status: 'disabled' };
    }

    return { status: 'inactive' };
  }, [fileUploads, hasAccess, input, isPending, isPreparingUpload, queries]);

  const submitCtx = useMemo(
    () => ({
      state: submitState,
      onSubmit: handleSubmit,
    }),
    [submitState, handleSubmit]
  );

  return <SubmitMessageContext.Provider value={submitCtx}>{props.children}</SubmitMessageContext.Provider>;
};
